/**
 * Excel Export Utilities using ExcelJS
 * Secure replacement for xlsx package
 *
 * @description This module provides Excel export functionality using ExcelJS
 * to replace the vulnerable xlsx package while maintaining the same API interface.
 */

import ExcelJS from 'exceljs';

/**
 * Export data to a single-sheet Excel file
 * @param {Array} data - Array of objects to export
 * @param {Array} keys - Array of object keys to use as column headers
 * @param {string} fileName - Name of the file (without extension)
 * @param {boolean} autoWidth - Whether to auto-size columns
 */
export async function exportCsv(data, filename = 'export.xlsx', autoWidth = false) {
    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        if (data.length > 0) {
            // Add headers
            const headers = Object.keys(data[0]);
            worksheet.addRow(headers);

            // Add data rows
            data.forEach((row) => {
                const values = headers.map((header) => row[header] ?? '');
                worksheet.addRow(values);
            });

            // Apply auto-width if requested
            if (autoWidth) {
                worksheet.columns.forEach((column) => {
                    column.width = 15; // Default width for auto-width
                });
            }
        }

        // Generate Excel buffer
        const buffer = await workbook.xlsx.writeBuffer();

        // Convert Buffer to ArrayBuffer for blob compatibility
        // Node.js Buffer inherits from Uint8Array, we need to create a proper ArrayBuffer
        const arrayBuffer = new ArrayBuffer(buffer.length);
        const view = new Uint8Array(arrayBuffer);
        view.set(buffer);

        // Create blob and download
        const blob = new globalThis.Blob([arrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        downloadExcelFile(blob, filename);
    } catch (error) {
        console.error('Excel export failed:', error);
        throw error;
    }
}

/**
 * Export data to a multi-sheet Excel file
 * @param {Array} data - Array of data arrays (one per sheet)
 * @param {Array} key - Array of object keys to use as column headers
 * @param {Array} sheetName - Array of sheet names
 * @param {string} fileName - Name of the file (without extension)
 */
export async function exportMultipleCsv(data, key, sheetName, fileName) {
    try {
        const workbook = new ExcelJS.Workbook();

        // Process each sheet
        for (let i = 0; i < data.length; i++) {
            const sheetData = data[i];
            const currentSheetName = sheetName[i] || `Sheet${i + 1}`;
            const worksheet = workbook.addWorksheet(currentSheetName);

            if (sheetData && sheetData.length > 0) {
                // Add headers first
                worksheet.addRow(key);

                // Add data rows
                sheetData.forEach((row) => {
                    const values = key.map((header) => row[header] ?? '');
                    worksheet.addRow(values);
                });

                // Apply auto-width (matching original behavior)
                const jsonArray = sheetData.map((v) => key.map((j) => v[j]));

                // Calculate column widths
                const colWidth = [key, ...jsonArray].map((row) =>
                    row.map((val) => {
                        if (val == null) {
                            return { wch: 10 };
                        } else {
                            return { wch: Math.min(Math.max(val.toString().length, 10), 50) };
                        }
                    })
                );

                let result = colWidth[0];
                for (let j = 1; j < colWidth.length; j++) {
                    for (let k = 0; k < colWidth[j].length; k++) {
                        if (result[k]['wch'] < colWidth[j][k]['wch']) {
                            result[k]['wch'] = colWidth[j][k]['wch'];
                        }
                    }
                }

                // Apply calculated widths
                worksheet.columns = result.map((col) => ({ width: col.wch }));
            }
        }

        // Generate Excel buffer
        const buffer = await workbook.xlsx.writeBuffer();

        // Convert Buffer to ArrayBuffer for blob compatibility
        const arrayBuffer = new ArrayBuffer(buffer.length);
        const view = new Uint8Array(arrayBuffer);
        view.set(buffer);

        // Create blob and download
        const blob = new globalThis.Blob([arrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        downloadExcelFile(blob, fileName + '.xlsx');
    } catch (error) {
        console.error('Excel multi-sheet export failed:', error);
        throw error;
    }
}

/**
 * Helper function to download Excel file
 * @param {ArrayBuffer} buffer - Excel file buffer
 * @param {string} filename - Name of the file with extension
 */
const downloadExcelFile = (buffer, filename) => {
    try {
        // Create blob with correct MIME type for Excel files
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        // Check if we're in a test environment
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            // In test environment, just return success
            return;
        }

        // Create download URL
        const url = window.URL.createObjectURL(blob);

        // Create temporary download link
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        // Check if element supports style property (test environment compatibility)
        if (link.style) {
            link.style.display = 'none';
        }

        // Trigger download
        if (document.body) {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Fallback for test environments
            link.click();
        }

        // Clean up
        if (window.URL && window.URL.revokeObjectURL) {
            window.URL.revokeObjectURL(url);
        }
    } catch (error) {
        console.error('Error downloading Excel file:', error);
        throw new Error(`Failed to download Excel file: ${error.message}`);
    }
};

/**
 * Legacy function names for backward compatibility
 * These maintain the exact same API as the xlsx implementation
 */

// Helper functions that were used in the original xlsx implementation
export const jsonToArray = (keys, jsonData) => {
    return jsonData.map((row) => keys.map((key) => row[key] || ''));
};

export const columnWidth = (data, keys) => {
    if (!data || !keys || data.length === 0) return [];

    return keys.map((key) => {
        let maxLength = key?.toString().length || 10;

        data.forEach((row) => {
            const value = row[key]?.toString() || '';
            maxLength = Math.max(maxLength, value.length);
        });

        return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
};

// Export default functions for easier importing
export default {
    exportCsv,
    exportMultipleCsv,
    jsonToArray,
    columnWidth,
};
